import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Calendar, Settings, LogOut, Camera,
  Save, Edit2, Phone, Globe, Clock, Link2, Loader2
} from 'lucide-react';
import { authService, ProfilePayload } from '../services/auth.service.ts';
import UserAvatar from '../components/UserAvatar.tsx';

const PROVIDER_LABELS: Record<string, { label: string; bg: string; text: string }> = {
  github: { label: 'GitHub', bg: 'bg-gray-900', text: 'text-white' },
  google: { label: 'Google', bg: 'bg-white', text: 'text-gray-800' },
  linkedin: { label: 'LinkedIn', bg: 'bg-[#0a66c2]', text: 'text-white' },
};

const LANGUAGE_OPTIONS = ['Türkçe', 'English'];
const TIMEZONE_OPTIONS = ['Europe/Istanbul', 'UTC', 'Europe/London', 'Europe/Berlin'];
const DATE_FORMAT_OPTIONS = ['GG/AA/YYYY', 'AA/GG/YYYY', 'YYYY-AA-GG'];
const TIME_FORMAT_OPTIONS = ['24 saat', '12 saat'];

function createProfileForm(user: any): ProfilePayload {
  return {
    name: user?.name || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    language: user?.language || 'Türkçe',
    timezone: user?.timezone || 'Europe/Istanbul',
    dateFormat: user?.dateFormat || 'GG/AA/YYYY',
    timeFormat: user?.timeFormat || '24 saat',
  };
}

function displayValue(value?: string | null, placeholder = 'Belirtilmedi') {
  return value && value.trim() ? value : placeholder;
}

function Profile() {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(authService.getUser());
  const [form, setForm] = useState<ProfilePayload>(createProfileForm(authService.getUser()));

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    let active = true;

    const loadProfile = async () => {
      try {
        const user = await authService.fetchProfile();
        if (!active) return;
        setCurrentUser(user);
        setForm(createProfileForm(user));
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || 'Profil bilgileri yüklenemedi.');
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [navigate]);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    authService.logout();
    setShowLogoutConfirm(false);
    navigate('/');
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const startEditing = () => {
    setError('');
    setSuccess('');
    setForm(createProfileForm(currentUser));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setForm(createProfileForm(currentUser));
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const updatedUser = await authService.updateProfile(form);
      setCurrentUser(updatedUser);
      setForm(createProfileForm(updatedUser));
      setIsEditing(false);
      setSuccess('Profil bilgileri kaydedildi.');
    } catch (err: any) {
      setError(err?.message || 'Profil güncellenemedi.');
    } finally {
      setIsSaving(false);
    }
  };

  const provider = (currentUser?.provider as 'github' | 'google' | 'linkedin' | null) || null;
  const providerMeta = provider ? PROVIDER_LABELS[provider] : null;
  const joinDate = currentUser?.createdAt
    ? new Date(currentUser.createdAt).toLocaleDateString('tr-TR')
    : '-';

  if (isLoading) {
    return (
      <div className="bg-slate-800/80 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-8 flex items-center justify-center gap-3 text-gray-300">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Profil yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/80 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 lg:p-8">
        {(error || success) && (
          <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
            error
              ? 'bg-red-500/10 border-red-500/30 text-red-300'
              : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
          }`}>
            {error || success}
          </div>
        )}

        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="relative shrink-0">
            <UserAvatar
              src={currentUser?.avatar}
              name={currentUser?.name}
              email={currentUser?.email}
              provider={provider}
              showProviderBadge={!!provider}
              sizeClass="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32"
              className="ring-2 ring-white/10 rounded-full"
            />
            {isEditing && (
              <button
                className="absolute bottom-0 right-0 p-1.5 sm:p-2 bg-purple-600 rounded-full hover:bg-purple-700 transition"
                aria-label="Fotoğraf değiştir"
                title="Avatar yükleme yakında"
                type="button"
              >
                <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </button>
            )}
          </div>

          <div className="flex-1 text-center md:text-left min-w-0 w-full">
            {isEditing ? (
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="text-xl sm:text-2xl lg:text-3xl font-bold text-white bg-white/5 border border-white/10 rounded-lg px-4 py-2 mb-2 w-full focus:outline-none focus:border-purple-500"
                aria-label="İsim"
              />
            ) : (
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2">{currentUser?.name || 'Kullanıcı'}</h1>
            )}

            <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400 mb-2">
              <Mail className="w-4 h-4" />
              <span className="text-sm lg:text-base break-all">{currentUser?.email}</span>
            </div>
            <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400">
              <Calendar className="w-4 h-4" />
              <span className="text-sm lg:text-base">Üyelik: {joinDate}</span>
            </div>
          </div>

          <div className="w-full md:w-auto flex justify-center md:justify-end gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={cancelEditing}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white/10 hover:bg-white/20 text-white rounded-lg"
                  type="button"
                  disabled={isSaving}
                >
                  İptal
                </button>
                <button
                  onClick={handleSave}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg disabled:opacity-70"
                  type="button"
                  disabled={isSaving}
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{isSaving ? 'Kaydediliyor' : 'Kaydet'}</span>
                </button>
              </>
            ) : (
              <button onClick={startEditing} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white/10 hover:bg-white/20 text-white rounded-lg" type="button">
                <Edit2 className="w-4 h-4" />
                <span>Düzenle</span>
              </button>
            )}
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <h2 className="text-base sm:text-lg font-semibold text-white mb-3">Hakkımda</h2>
          {isEditing ? (
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
              rows={4}
              maxLength={500}
              aria-label="Hakkımda"
            />
          ) : (
            <p className="text-gray-300 bg-white/5 rounded-lg p-4">{displayValue(currentUser?.bio, 'Henüz bir bio eklenmemiş.')}</p>
          )}
        </div>

        <div className="mb-6 md:mb-8">
          <h2 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-purple-400" />
            Hesap Bağlantısı
          </h2>
          <div className="bg-white/5 rounded-lg p-4 border border-white/10 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-300">
              {providerMeta ? (
                <>Bu hesap <span className="font-semibold text-white">{providerMeta.label}</span> ile bağlı.</>
              ) : (
                <>Bu hesap <span className="font-semibold text-white">e-posta &amp; şifre</span> ile oluşturuldu.</>
              )}
            </div>
            {providerMeta && (
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${providerMeta.bg} ${providerMeta.text}`}>
                {providerMeta.label}
              </span>
            )}
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <h2 className="text-base sm:text-lg font-semibold text-white mb-4">Kişisel</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center space-x-3 mb-2">
                <Phone className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Telefon</span>
              </div>
              {isEditing ? (
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  placeholder="+90 5xx xxx xx xx"
                />
              ) : (
                <p className="text-white">{displayValue(currentUser?.phone)}</p>
              )}
            </div>

            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center space-x-3 mb-2">
                <Globe className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Dil</span>
              </div>
              {isEditing ? (
                <select
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  aria-label="Dil"
                  title="Dil"
                >
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <p className="text-white">{displayValue(currentUser?.language)}</p>
              )}
            </div>

            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center space-x-3 mb-2">
                <Clock className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Saat Dilimi</span>
              </div>
              {isEditing ? (
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  aria-label="Saat Dilimi"
                  title="Saat Dilimi"
                >
                  {TIMEZONE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <p className="text-white">{displayValue(currentUser?.timezone)}</p>
              )}
            </div>

            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center space-x-3 mb-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Tarih Biçimi</span>
              </div>
              {isEditing ? (
                <select
                  value={form.dateFormat}
                  onChange={(e) => setForm({ ...form, dateFormat: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  aria-label="Tarih Biçimi"
                  title="Tarih Biçimi"
                >
                  {DATE_FORMAT_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <p className="text-white">{displayValue(currentUser?.dateFormat)}</p>
              )}
            </div>

            <div className="bg-white/5 rounded-lg p-4 border border-white/10 sm:col-span-2">
              <div className="flex items-center space-x-3 mb-2">
                <Clock className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Saat Biçimi</span>
              </div>
              {isEditing ? (
                <select
                  value={form.timeFormat}
                  onChange={(e) => setForm({ ...form, timeFormat: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  aria-label="Saat Biçimi"
                  title="Saat Biçimi"
                >
                  {TIME_FORMAT_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <p className="text-white">{displayValue(currentUser?.timeFormat)}</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-4 flex flex-wrap gap-2">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition"
            aria-label="Ayarlar"
            type="button"
          >
            <Settings className="w-4 h-4 text-gray-400" />
            <span className="text-white">Ayarlar</span>
          </button>
          <button
            onClick={handleLogoutClick}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-red-600/20 hover:bg-red-600/30 rounded-lg transition"
            aria-label="Çıkış Yap"
            type="button"
          >
            <LogOut className="w-4 h-4 text-red-400" />
            <span className="text-red-400">Çıkış Yap</span>
          </button>
        </div>
      </div>

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-white/10 p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Çıkış Yap</h3>
              <p className="text-gray-400 mb-6">Hesabınızdan çıkış yapmak istediğinize emin misiniz?</p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelLogout}
                  className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition"
                  type="button"
                >
                  İptal
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition"
                  type="button"
                >
                  Çıkış Yap
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;